import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Card, Empty, Skeleton, Space, Tag, Typography } from 'antd';
import { productApi } from '../../api';

/**
 * Teacher — Section roster. P-03 subset: teacher only sees a section they
 * actually teach, and only sees name + adm no + primary guardian. No fees,
 * no address, no health notes.
 */
export function TeacherRosterPage() {
  const { institution = '', grade = '', section = '' } = useParams<{ institution: string; grade: string; section: string }>();
  const q = useQuery({
    queryKey: ['teacher-roster', institution, grade, section],
    queryFn: () => productApi.getTeacherRoster(institution, grade, section),
    enabled: !!institution && !!grade && !!section,
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 6 }} />;
  const r = q.data;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {r.section.grade}{r.section.section ? `-${r.section.section}` : ''}
        </Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          {r.students.length} student{r.students.length === 1 ? '' : 's'}
        </Typography.Text>
      </div>

      {!r.authorised ? (
        <Alert
          type="warning" showIcon
          message="You don't teach this section"
          description="Only teachers assigned in the timetable can open a section's roster."
        />
      ) : r.students.length === 0 ? (
        <Empty description="No enrolled students in this section." style={{ padding: 20 }} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
          {r.students.map((s) => (
            <Card key={s.student_id} size="small">
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: s.gender === 'female' ? '#fbe6f2' : s.gender === 'male' ? '#e6f0fb' : '#eef0f3',
                  color: '#3f4a63', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700,
                }}>
                  {s.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{s.name}</div>
                  <Tag style={{ marginTop: 2 }}>{s.admission_number}</Tag>
                  {s.primary_guardian_name && (
                    <div style={{ fontSize: 11.5, color: '#5b6478', marginTop: 6 }}>
                      {s.primary_guardian_name} · {s.primary_guardian_phone ?? ''}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Space>
  );
}
